import React, { useMemo, useRef, useState } from "react";
import JSZip from "jszip";

const DEFAULT_MONTHLY_LIMIT = 500000;
const TRANSLATE_BATCH_CHAR_LIMIT = 24000;
const TRANSLATE_BATCH_ITEM_LIMIT = 60;

function normalizePath(path) {
  const parts = String(path || "").split("/");
  const stack = [];

  parts.forEach((part) => {
    if (!part || part === ".") {
      return;
    }

    if (part === "..") {
      stack.pop();
      return;
    }

    stack.push(part);
  });

  return stack.join("/");
}

function dirname(path) {
  const normalized = normalizePath(path);
  const index = normalized.lastIndexOf("/");
  return index >= 0 ? normalized.slice(0, index) : "";
}

function joinPath(base, href) {
  if (!base) {
    return normalizePath(href);
  }

  return normalizePath(`${base}/${href}`);
}

function escapeXml(text) {
  return String(text || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

function slugifyFileName(value) {
  return String(value || "translated-epub")
    .trim()
    .replace(/\.[^.]+$/, "")
    .replace(/[^a-z0-9\u4e00-\u9fff]+/gi, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80) || "translated-epub";
}

function getTextContent(node) {
  return String(node?.textContent || "").replace(/\s+/g, " ").trim();
}

function decodeBasicEntities(text) {
  return String(text || "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_match, code) => String.fromCodePoint(Number(code)))
    .replace(/&#x([0-9a-f]+);/gi, (_match, code) => String.fromCodePoint(Number.parseInt(code, 16)));
}

function stripTags(text) {
  return decodeBasicEntities(String(text || "").replace(/<[^>]+>/g, " ")).replace(/\s+/g, " ").trim();
}

function getAttributeFromTag(tag, name) {
  const match = String(tag || "").match(new RegExp(`${name}\\s*=\\s*(["'])(.*?)\\1`, "i"));
  return match ? decodeBasicEntities(match[2]) : "";
}

function getFirstXmlText(xml, localName) {
  const pattern = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b[^>]*>([\\s\\S]*?)<\\/(?:[\\w.-]+:)?${localName}>`, "i");
  const match = String(xml || "").match(pattern);
  return match ? stripTags(match[1]) : "";
}

function getFirstTagAttribute(xml, localName, attributeName) {
  const pattern = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b[^>]*>`, "i");
  const match = String(xml || "").match(pattern);
  return match ? getAttributeFromTag(match[0], attributeName) : "";
}

function getTags(xml, localName) {
  const pattern = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b[^>]*>`, "gi");
  return String(xml || "").match(pattern) || [];
}

function getTagRange(xml, localName) {
  const pattern = new RegExp(`<(?:[\\w.-]+:)?${localName}\\b[^>]*>[\\s\\S]*?<\\/(?:[\\w.-]+:)?${localName}>`, "i");
  const match = String(xml || "").match(pattern);
  return match ? match[0] : "";
}

function findOpfPath(zip) {
  return zip.file("META-INF/container.xml")
    ?.async("text")
    .then((containerText) => {
      const fullPath = getFirstTagAttribute(containerText, "rootfile", "full-path");

      if (!fullPath) {
        throw new Error("EPUB container does not point to an OPF package.");
      }

      return normalizePath(fullPath);
    });
}

function extractBlocksFromHtml(html, chapterTitle) {
  if (typeof DOMParser !== "function") {
    const fallbackBlocks = String(html || "")
      .replace(/<(script|style|nav|aside)\b[^>]*>[\s\S]*?<\/\1>/gi, " ")
      .match(/<(h[1-6]|p|blockquote|li|td|th|figcaption)\b[^>]*>[\s\S]*?<\/\1>/gi);

    return (fallbackBlocks || [])
      .map((block, index) => {
        const tagName = block.match(/^<([a-z0-9]+)/i)?.[1]?.toLowerCase() || "p";
        const text = stripTags(block);

        return text
          ? {
              id: `${chapterTitle || "chapter"}-${index}`,
              text,
              tagName: /^h[1-6]$/.test(tagName) ? tagName : "p",
              sourceChars: text.length
            }
          : null;
      })
      .filter(Boolean);
  }

  const doc = new DOMParser().parseFromString(String(html || ""), "text/html");
  const body = doc.body;

  if (!body) {
    return [];
  }

  body.querySelectorAll("script, style, nav, aside").forEach((node) => node.remove());

  const candidates = Array.from(
    body.querySelectorAll("h1,h2,h3,h4,h5,h6,p,blockquote,li,td,th,figcaption")
  );
  const sourceNodes = candidates.length ? candidates : Array.from(body.children);
  const seen = new Set();

  return sourceNodes
    .map((node, index) => {
      const text = getTextContent(node);

      if (!text || seen.has(text)) {
        return null;
      }

      seen.add(text);
      return {
        id: `${chapterTitle || "chapter"}-${index}`,
        text,
        tagName: /^h[1-6]$/i.test(node.tagName) ? node.tagName.toLowerCase() : "p",
        sourceChars: text.length
      };
    })
    .filter(Boolean);
}

async function readEpub(file) {
  const zip = await JSZip.loadAsync(file);
  const opfPath = await findOpfPath(zip);
  const opfText = await zip.file(opfPath)?.async("text");

  if (!opfText) {
    throw new Error("EPUB package file was not found.");
  }

  const opfDir = dirname(opfPath);
  const title = getFirstXmlText(opfText, "title") || file.name.replace(/\.epub$/i, "");
  const creator = getFirstXmlText(opfText, "creator");
  const manifestXml = getTagRange(opfText, "manifest");
  const spineXml = getTagRange(opfText, "spine");
  const manifestItems = new Map(
    getTags(manifestXml, "item").map((item) => [
      getAttributeFromTag(item, "id"),
      {
        href: getAttributeFromTag(item, "href"),
        mediaType: getAttributeFromTag(item, "media-type")
      }
    ])
  );

  const chapters = [];

  for (const itemref of getTags(spineXml, "itemref")) {
    const item = manifestItems.get(getAttributeFromTag(itemref, "idref"));

    if (!item || !/x?html/i.test(item.mediaType || "")) {
      continue;
    }

    const path = joinPath(opfDir, item.href);
    const html = await zip.file(path)?.async("text");

    if (!html) {
      continue;
    }

    const blocks = extractBlocksFromHtml(html, path);

    if (blocks.length) {
      chapters.push({
        path,
        title: blocks.find((block) => /^h[1-6]$/.test(block.tagName))?.text || `Chapter ${chapters.length + 1}`,
        blocks
      });
    }
  }

  const totalChars = chapters.reduce(
    (sum, chapter) => sum + chapter.blocks.reduce((blockSum, block) => blockSum + block.sourceChars, 0),
    0
  );

  if (!chapters.length || !totalChars) {
    throw new Error("No readable English text was found in this EPUB.");
  }

  return {
    fileName: file.name,
    title,
    creator,
    chapters,
    totalChars
  };
}

function selectBlocksForQuota(book, startOffset, charLimit) {
  let cursor = 0;
  let selectedChars = 0;
  const selectedChapters = [];

  book.chapters.forEach((chapter) => {
    const blocks = [];

    chapter.blocks.forEach((block) => {
      const blockStart = cursor;
      const blockEnd = cursor + block.sourceChars;
      cursor = blockEnd;

      if (blockEnd <= startOffset || selectedChars >= charLimit) {
        return;
      }

      if (blockStart < startOffset) {
        return;
      }

      if (selectedChars + block.sourceChars > charLimit) {
        return;
      }

      blocks.push({
        ...block,
        absoluteStart: blockStart,
        absoluteEnd: blockEnd
      });
      selectedChars += block.sourceChars;
    });

    if (blocks.length) {
      selectedChapters.push({
        ...chapter,
        blocks
      });
    }
  });

  return {
    chapters: selectedChapters,
    sourceChars: selectedChars,
    nextOffset: startOffset + selectedChars,
    skippedPartialBlock:
      selectedChars === 0 && startOffset < book.totalChars
        ? "Start position falls inside a paragraph. Use the previous completed offset shown by this app."
        : ""
  };
}

function makeTranslationBatches(blocks) {
  const batches = [];
  let current = [];
  let currentChars = 0;

  blocks.forEach((block) => {
    const nextWouldOverflow =
      current.length >= TRANSLATE_BATCH_ITEM_LIMIT ||
      currentChars + block.text.length > TRANSLATE_BATCH_CHAR_LIMIT;

    if (current.length && nextWouldOverflow) {
      batches.push(current);
      current = [];
      currentChars = 0;
    }

    current.push(block);
    currentChars += block.text.length;
  });

  if (current.length) {
    batches.push(current);
  }

  return batches;
}

async function translateBatch(blocks, targetLanguage) {
  const response = await fetch("/api/epub/translate-batch", {
    method: "POST",
    headers: {
      "Content-Type": "application/json"
    },
    body: JSON.stringify({
      targetLanguage,
      texts: blocks.map((block) => block.text)
    })
  });

  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload?.error || "DeepL translation failed.");
  }

  return payload.translations || [];
}

function buildChapterXhtml(chapter, chapterNumber) {
  const body = chapter.blocks
    .map((block) => {
      const tag = /^h[1-6]$/.test(block.tagName) ? block.tagName : "p";
      return `    <${tag}>${escapeXml(block.translatedText || block.text)}</${tag}>`;
    })
    .join("\n");

  return `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" lang="zh-CN" xml:lang="zh-CN">
  <head>
    <meta charset="utf-8"/>
    <title>${escapeXml(chapter.title || `第 ${chapterNumber} 章`)}</title>
    <link rel="stylesheet" type="text/css" href="../Styles/style.css"/>
  </head>
  <body>
${body}
  </body>
</html>`;
}

function buildContentOpf({ book, selected, generatedAt }) {
  const items = selected.chapters
    .map(
      (_chapter, index) =>
        `    <item id="chapter-${index + 1}" href="Text/chapter-${index + 1}.xhtml" media-type="application/xhtml+xml"/>`
    )
    .join("\n");
  const spine = selected.chapters
    .map((_chapter, index) => `    <itemref idref="chapter-${index + 1}"/>`)
    .join("\n");

  return `<?xml version="1.0" encoding="utf-8"?>
<package xmlns="http://www.idpf.org/2007/opf" unique-identifier="book-id" version="3.0">
  <metadata xmlns:dc="http://purl.org/dc/elements/1.1/">
    <dc:identifier id="book-id">urn:uuid:${crypto.randomUUID()}</dc:identifier>
    <dc:title>${escapeXml(`${book.title} 中文分册`)}</dc:title>
    ${book.creator ? `<dc:creator>${escapeXml(book.creator)}</dc:creator>` : ""}
    <dc:language>zh-CN</dc:language>
    <meta property="dcterms:modified">${generatedAt}</meta>
    <meta property="epub-translator:source-title">${escapeXml(book.title)}</meta>
    <meta property="epub-translator:start-offset">${selected.startOffset}</meta>
    <meta property="epub-translator:next-offset">${selected.nextOffset}</meta>
    <meta property="epub-translator:source-chars">${selected.sourceChars}</meta>
  </metadata>
  <manifest>
    <item id="nav" href="nav.xhtml" media-type="application/xhtml+xml" properties="nav"/>
    <item id="style" href="Styles/style.css" media-type="text/css"/>
${items}
  </manifest>
  <spine>
${spine}
  </spine>
</package>`;
}

function buildNav(book, selected) {
  const listItems = selected.chapters
    .map(
      (chapter, index) =>
        `      <li><a href="Text/chapter-${index + 1}.xhtml">${escapeXml(chapter.title || `第 ${index + 1} 章`)}</a></li>`
    )
    .join("\n");

  return `<?xml version="1.0" encoding="utf-8"?>
<!DOCTYPE html>
<html xmlns="http://www.w3.org/1999/xhtml" lang="zh-CN" xml:lang="zh-CN">
  <head>
    <meta charset="utf-8"/>
    <title>${escapeXml(book.title)} 目录</title>
  </head>
  <body>
    <nav epub:type="toc" id="toc">
      <h1>${escapeXml(book.title)} 中文分册</h1>
      <ol>
${listItems}
      </ol>
    </nav>
  </body>
</html>`;
}

async function buildTranslatedEpub(book, selected) {
  const zip = new JSZip();
  const generatedAt = new Date().toISOString().replace(/\.\d{3}Z$/, "Z");

  zip.file("mimetype", "application/epub+zip", {
    compression: "STORE"
  });
  zip.file(
    "META-INF/container.xml",
    `<?xml version="1.0" encoding="utf-8"?>
<container version="1.0" xmlns="urn:oasis:names:tc:opendocument:xmlns:container">
  <rootfiles>
    <rootfile full-path="OEBPS/content.opf" media-type="application/oebps-package+xml"/>
  </rootfiles>
</container>`
  );
  zip.file("OEBPS/content.opf", buildContentOpf({ book, selected, generatedAt }));
  zip.file("OEBPS/nav.xhtml", buildNav(book, selected));
  zip.file(
    "OEBPS/Styles/style.css",
    "body{font-family:serif;line-height:1.8;margin:5%;}p{margin:0 0 1em;}h1,h2,h3,h4,h5,h6{line-height:1.35;margin:1.4em 0 .7em;}"
  );

  selected.chapters.forEach((chapter, index) => {
    zip.file(`OEBPS/Text/chapter-${index + 1}.xhtml`, buildChapterXhtml(chapter, index + 1));
  });

  return zip.generateAsync({
    type: "blob",
    mimeType: "application/epub+zip",
    compression: "DEFLATE",
    compressionOptions: {
      level: 6
    }
  });
}

function downloadBlob(blob, fileName) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function EpubTranslator({ onBack }) {
  const fileInputRef = useRef(null);
  const [book, setBook] = useState(null);
  const [fileName, setFileName] = useState("");
  const [startOffset, setStartOffset] = useState(0);
  const [monthlyLimit, setMonthlyLimit] = useState(DEFAULT_MONTHLY_LIMIT);
  const [targetLanguage, setTargetLanguage] = useState("ZH-HANS");
  const [status, setStatus] = useState("请选择一本英文 EPUB。");
  const [isBusy, setIsBusy] = useState(false);
  const [progress, setProgress] = useState({
    done: 0,
    total: 0
  });
  const [lastResult, setLastResult] = useState(null);

  const selectionPreview = useMemo(() => {
    if (!book) {
      return null;
    }

    return selectBlocksForQuota(
      book,
      Math.max(0, Number(startOffset) || 0),
      Math.max(1, Number(monthlyLimit) || DEFAULT_MONTHLY_LIMIT)
    );
  }, [book, monthlyLimit, startOffset]);

  async function handleFileChange(event) {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    setIsBusy(true);
    setLastResult(null);
    setStatus("正在解析 EPUB...");

    try {
      const parsedBook = await readEpub(file);
      setBook(parsedBook);
      setFileName(file.name);
      setStartOffset(0);
      setStatus(`已读取《${parsedBook.title}》，共 ${parsedBook.totalChars.toLocaleString()} 个英文字符。`);
    } catch (error) {
      setBook(null);
      setStatus(error instanceof Error ? error.message : "EPUB 解析失败。");
    } finally {
      setIsBusy(false);
      event.target.value = "";
    }
  }

  async function handleTranslate() {
    if (!book || !selectionPreview?.sourceChars) {
      setStatus("当前没有可翻译内容，请检查起始位置和字符额度。");
      return;
    }

    const selected = {
      ...selectionPreview,
      startOffset: Math.max(0, Number(startOffset) || 0)
    };
    const flatBlocks = selected.chapters.flatMap((chapter) =>
      chapter.blocks.map((block) => ({
        ...block,
        chapterPath: chapter.path
      }))
    );
    const batches = makeTranslationBatches(flatBlocks);
    const translationsById = new Map();

    setIsBusy(true);
    setProgress({ done: 0, total: flatBlocks.length });
    setStatus(`开始调用 DeepL，共 ${batches.length} 批。`);

    try {
      let translatedBlockCount = 0;

      for (let index = 0; index < batches.length; index += 1) {
        const batch = batches[index];
        setStatus(`正在翻译第 ${index + 1}/${batches.length} 批...`);
        const translations = await translateBatch(batch, targetLanguage);

        batch.forEach((block, blockIndex) => {
          translationsById.set(`${block.chapterPath}:${block.id}`, translations[blockIndex] || block.text);
        });

        translatedBlockCount += batch.length;
        setProgress({ done: translatedBlockCount, total: flatBlocks.length });
      }

      const translatedSelection = {
        ...selected,
        chapters: selected.chapters.map((chapter) => ({
          ...chapter,
          blocks: chapter.blocks.map((block) => ({
            ...block,
            translatedText: translationsById.get(`${chapter.path}:${block.id}`) || block.text
          }))
        }))
      };
      const blob = await buildTranslatedEpub(book, translatedSelection);
      const partNumber = Math.floor(selected.startOffset / Math.max(1, Number(monthlyLimit) || DEFAULT_MONTHLY_LIMIT)) + 1;
      const outputName = `${slugifyFileName(book.title)}-中文分册-${partNumber}-${selected.startOffset}-${selected.nextOffset}.epub`;
      downloadBlob(blob, outputName);

      setLastResult({
        outputName,
        startOffset: selected.startOffset,
        nextOffset: selected.nextOffset,
        sourceChars: selected.sourceChars
      });
      setStartOffset(selected.nextOffset);
      setStatus("翻译完成，EPUB 已下载。下次可直接使用新的起始位置继续。");
    } catch (error) {
      setStatus(error instanceof Error ? error.message : "翻译失败。");
    } finally {
      setIsBusy(false);
    }
  }

  return (
    <main className="epubTranslatorPage">
      <section className="epubTranslatorHero">
        <div>
          <p className="epubTranslatorEyebrow">DeepL EPUB Translator</p>
          <h1>英文 EPUB 分月翻译成中文</h1>
          <p>
            上传一本英文 EPUB，设置本月字符额度，应用会翻译对应区间并生成一个中文 EPUB 分册。
            下个月用上次显示的起始位置继续，直到整本书完成。
          </p>
        </div>
        <button className="epubTranslatorBackButton" type="button" onClick={onBack}>
          返回
        </button>
      </section>

      <section className="epubTranslatorWorkspace">
        <div className="epubTranslatorPanel">
          <div className="epubTranslatorUpload">
            <input
              ref={fileInputRef}
              className="epubTranslatorFileInput"
              type="file"
              accept=".epub,application/epub+zip"
              onChange={handleFileChange}
            />
            <button
              className="epubTranslatorUploadButton"
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isBusy}
            >
              选择 EPUB
            </button>
            <span>{fileName || "尚未选择文件"}</span>
          </div>

          <div className="epubTranslatorControls">
            <label>
              <span>起始字符位置</span>
              <input
                type="number"
                min="0"
                step="1"
                value={startOffset}
                onChange={(event) => setStartOffset(event.target.value)}
                disabled={isBusy}
              />
            </label>
            <label>
              <span>本月字符额度</span>
              <input
                type="number"
                min="1000"
                step="1000"
                value={monthlyLimit}
                onChange={(event) => setMonthlyLimit(event.target.value)}
                disabled={isBusy}
              />
            </label>
            <label>
              <span>中文类型</span>
              <select
                value={targetLanguage}
                onChange={(event) => setTargetLanguage(event.target.value)}
                disabled={isBusy}
              >
                <option value="ZH-HANS">简体中文</option>
                <option value="ZH-HANT">繁体中文</option>
              </select>
            </label>
          </div>

          <button
            className="epubTranslatorPrimaryButton"
            type="button"
            onClick={handleTranslate}
            disabled={isBusy || !book || !selectionPreview?.sourceChars}
          >
            {isBusy ? "处理中..." : "翻译并下载中文 EPUB"}
          </button>

          <div className="epubTranslatorStatus" role="status" aria-live="polite">
            {status}
          </div>

          {progress.total ? (
            <div className="epubTranslatorProgress">
              <span style={{ width: `${Math.round((progress.done / progress.total) * 100)}%` }} />
            </div>
          ) : null}
        </div>

        <aside className="epubTranslatorPanel epubTranslatorStats">
          <h2>本次范围</h2>
          <dl>
            <div>
              <dt>整本英文字符</dt>
              <dd>{book ? book.totalChars.toLocaleString() : "-"}</dd>
            </div>
            <div>
              <dt>本次将翻译</dt>
              <dd>{selectionPreview ? selectionPreview.sourceChars.toLocaleString() : "-"}</dd>
            </div>
            <div>
              <dt>下次起始位置</dt>
              <dd>{selectionPreview ? selectionPreview.nextOffset.toLocaleString() : "-"}</dd>
            </div>
            <div>
              <dt>章节数量</dt>
              <dd>{selectionPreview ? selectionPreview.chapters.length : "-"}</dd>
            </div>
          </dl>
          {selectionPreview?.skippedPartialBlock ? (
            <p className="epubTranslatorWarning">{selectionPreview.skippedPartialBlock}</p>
          ) : null}
          {lastResult ? (
            <div className="epubTranslatorResult">
              <strong>刚生成：</strong>
              <span>{lastResult.outputName}</span>
              <span>下次从 {lastResult.nextOffset.toLocaleString()} 开始。</span>
            </div>
          ) : null}
          <p className="epubTranslatorNote">
            需要在 Vercel 环境变量中配置 DEEPL_AUTH_KEY。DeepL 免费额度按源文本字符计费，本工具会尽量不超过你设置的额度。
          </p>
        </aside>
      </section>
    </main>
  );
}
